import { Typography } from '@atlas-metrics/design-system';
import React, { useEffect, useRef } from 'react';

import clsxm from '@/lib/clsxm';

import { SubNavigationItem, SubNavigationItemProps } from './SubNavigationItem';

export interface SubNavigationSectionProps {
  title: string;
  items: Omit<SubNavigationItemProps, 'onClickHandler' | 'fontColor'>[];
}

export interface SubNavigationProps {
  /** Selected item id. */
  selected?: string;

  /** Label of item. item should of type
   * { title: string;
   *   items: Omit<SubNavigationItemProps, 'onClickHandler'>[];
   * } */
  sections: SubNavigationSectionProps[];

  /** On Click handler whn any item is clicked. */
  onClickHandler: (id: string) => void;

  /** Override or extend the styles applied to the item component. */
  itemClassName?: string;

  /** Override or extend the styles applied to the component. */
  className?: string;
  /** Font color of the item */
  fontColor: string;
}

export const SubNavigation: React.FC<SubNavigationProps> = ({
  selected,
  sections,
  onClickHandler,
  className,
  itemClassName,
  fontColor,
}) => {
  const isFirstItemSelected = sections.some(
    ({ items }) => items[0].id === selected
  );

  const ref = useRef<null | HTMLDivElement>(null);

  const scrollToElement = () => {
    ref.current?.scrollIntoView({
      block: 'nearest',
      inline: 'start',
    });
  };

  useEffect(() => {
    if (selected && ref.current) {
      scrollToElement();
    }
  }, [selected]);

  return (
    <div
      style={{ maxHeight: `calc(100vh - ${130}px)` }}
      className={clsxm(
        'flex min-w-[250px] flex-col items-start gap-3 bg-white p-2',
        className
      )}
      data-testid='sub-nav'
    >
      {sections.map(({ title, items }, idx) => (
        <div className='flex w-full flex-col gap-1' key={title}>
          {/* if selected item is first, scroll to top of section */}
          <div ref={isFirstItemSelected && idx === 0 ? ref : null}>
            <Typography variant='body-m' bold className='px-2'>
              {title}
            </Typography>
          </div>
          <div className='flex flex-col items-start'>
            {items.map(({ id, label }) => (
              <div
                className='w-full'
                key={id}
                ref={id === selected && !isFirstItemSelected ? ref : null}
              >
                <SubNavigationItem
                  id={id}
                  label={label}
                  className={itemClassName}
                  onClickHandler={onClickHandler}
                  selected={id === selected}
                  fontColor={fontColor}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
